import Link from 'next/link';
import { ResortPhotosData } from '@/components/widget/types';
import { ImageComponent } from '@/components/common/ImageComponent';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';

interface Props {
  photos: ResortPhotosData;
  shouldShowViewAll?: boolean;
}

// Hero photo gallery: 1 large image on top + 2 side-by-side below.
// "View all" overlay sits on the second tile with a blue tint.

const sectionClass = '[&_>div>div_img]:rounded-[20px]';

const mainImageClass = 'mb-4';

const rowClass = [
  'flex flex-row justify-between flex-1',
  '[&>div]:flex-1',
  '[&>div:first-child]:mr-4',
  '[&>div:last-child]:relative',
  '[&>div:last-child>img]:!h-full [&>div:last-child>img]:object-cover [&>div:last-child>img]:object-center',
].join(' ');

const h100Class = '!h-full';

const allImagesButtonClass = [
  'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-max',
  'pt-[22px] px-[25px] pb-5 bg-white text-[#3B3B3B] rounded-[10px]',
  'font-medium uppercase tracking-[1px]',
  'text-[clamp(0.5rem,2vw,1rem)]',
  'hover:invert hover:no-underline [transition-duration:0.33s]',
  'max-sm:px-3 max-sm:py-[11px]',
].join(' ');

const blueOverlayClass = [
  'absolute inset-0 w-full h-full bg-[rgba(0,128,255,0.5)] rounded-[20px]',
  'pointer-events-none',
].join(' ');

export const ImagesSection = ({ photos, shouldShowViewAll }: Props) => {
  const { t } = useOTSTranslation('common');
  if (photos.length < 3) {
    return null;
  }

  return (
    <section className={sectionClass}>
      <div className="flex flex-col">
        <div className={mainImageClass}>
          <ImageComponent
            alt={photos[0].name}
            src={photos[0].cdnUrl}
          />
        </div>
        <div className={rowClass}>
          <div>
            <ImageComponent
              alt={photos[1].name}
              src={photos[1].cdnUrl}
              className={h100Class}
            />
          </div>
          <div>
            <ImageComponent
              alt={photos[2].name}
              src={photos[2].cdnUrl}
              className={h100Class}
            />
            {shouldShowViewAll && (
              <>
                <div className={blueOverlayClass} />
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link href="#" className={allImagesButtonClass}>
                  {t('common.viewAll')}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
